/** guardian/family — HOOKS. TanStack Query wrappers with invalidation. */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { guardianFamilyApi } from './api';
import { guardianFamilyKeys } from './keys';
import type {
  CreateStudentPayload,
  InviteGuardianPayload,
  UpdateGuardianProfilePayload,
  UpdateStudentPayload,
} from './types';

const invalidate = (qc: QueryClient) => qc.invalidateQueries({ queryKey: guardianFamilyKeys.all() });

export function useMyFamily() {
  return useQuery({
    queryKey: guardianFamilyKeys.overview(),
    queryFn: () => guardianFamilyApi.getMyFamily(),
  });
}

export function useUpdateGuardianProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateGuardianProfilePayload) =>
      guardianFamilyApi.updateGuardianProfile(payload),
    onSuccess: () => invalidate(qc),
  });
}

export function useCreateFamilyStudent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateStudentPayload) => guardianFamilyApi.createStudent(payload),
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateFamilyStudent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; payload: UpdateStudentPayload }) =>
      guardianFamilyApi.updateStudent(v.id, v.payload),
    onSuccess: () => invalidate(qc),
  });
}

export function useRemoveFamilyStudent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => guardianFamilyApi.removeStudent(id),
    onSuccess: () => invalidate(qc),
  });
}

export function useUploadStudentPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; blob: Blob }) => guardianFamilyApi.uploadStudentPhoto(v.id, v.blob),
    onSuccess: () => invalidate(qc),
  });
}

export function useUploadGuardianPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (blob: Blob) => guardianFamilyApi.uploadGuardianPhoto(blob),
    onSuccess: () => invalidate(qc),
  });
}

export function useInviteGuardian() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: InviteGuardianPayload) => guardianFamilyApi.inviteGuardian(payload),
    onSuccess: () => invalidate(qc),
  });
}

export function useRemoveGuardian() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => guardianFamilyApi.removeGuardian(id),
    onSuccess: () => invalidate(qc),
  });
}

export function useFamilyDevices() {
  return useQuery({
    queryKey: guardianFamilyKeys.devices(),
    queryFn: () => guardianFamilyApi.listDevices(),
  });
}

export function useLinkFamilyDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => guardianFamilyApi.linkDevice(name),
    onSuccess: () => invalidate(qc),
  });
}

export function useRevokeFamilyDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => guardianFamilyApi.revokeDevice(id),
    onSuccess: () => invalidate(qc),
  });
}
